import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type {
  DashboardStats,
  CategorySummary,
  ProfileSummary,
  PostCard,
  PostEngager,
  EngagementFilters,
  PostFilters,
} from "@/types/company-tracking";

export const useDashboardStats = () => {
  return useQuery({
    queryKey: ["company-tracking-stats"],
    queryFn: async (): Promise<DashboardStats | null> => {
      const { data, error } = await (supabase as any)
        .from("social_listening_dashboard_stats")
        .select("*")
        .single();

      if (error) throw error;
      return data as DashboardStats;
    },
  });
};

export const useCategorySummary = () => {
  return useQuery({
    queryKey: ["company-tracking-categories"],
    queryFn: async (): Promise<CategorySummary[]> => {
      const { data, error } = await (supabase as any)
        .from("social_listening_category_summary")
        .select("*")
        .order("total_engagements", { ascending: false });

      if (error) throw error;
      return (data || []) as CategorySummary[];
    },
  });
};

export const useProfileSummary = (category?: string) => {
  return useQuery({
    queryKey: ["company-tracking-profiles", category],
    queryFn: async (): Promise<ProfileSummary[]> => {
      let query = (supabase as any)
        .from("social_listening_profile_summary")
        .select("*")
        .order("total_engagements", { ascending: false });

      if (category) {
        query = query.eq("category", category);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data || []) as ProfileSummary[];
    },
  });
};

export const usePostEngagers = (postUrl: string | null) => {
  return useQuery({
    queryKey: ["company-tracking-engagers", postUrl],
    queryFn: async (): Promise<PostEngager[]> => {
      if (!postUrl) return [];

      const { data, error } = await (supabase as any)
        .from("social_listening_post_engagers")
        .select("*")
        .eq("post_url", postUrl)
        .order("is_top_account", { ascending: false });

      if (error) throw error;
      return (data || []) as PostEngager[];
    },
    enabled: !!postUrl,
  });
};

export const usePostsTable = (filters: PostFilters, search: string, page: number = 1) => {
  const pageSize = 50;

  return useQuery({
    queryKey: ["company-tracking-posts-table", filters, search, page],
    queryFn: async () => {
      let query = (supabase as any)
        .from("social_listening_post_cards")
        .select("*", { count: "exact" });

      if (search) {
        query = query.or(`post_content.ilike.%${search}%,monitoring_profile.ilike.%${search}%`);
      }

      if (filters.profile?.length) {
        query = query.in("monitoring_profile", filters.profile);
      }

      if (filters.dateRange && filters.dateRange !== "all") {
        const now = new Date();
        let daysAgo = 0;
        switch (filters.dateRange) {
          case "7d": daysAgo = 7; break;
          case "30d": daysAgo = 30; break;
          case "90d": daysAgo = 90; break;
        }
        if (daysAgo > 0) {
          const since = new Date(now.getTime() - daysAgo * 24 * 60 * 60 * 1000).toISOString();
          query = query.gte("post_date", since);
        }
      }

      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;
      query = query.order("post_date", { ascending: false }).range(from, to);

      const { data, error, count } = await query;
      if (error) throw error;

      return {
        data: (data || []) as PostCard[],
        count: count || 0,
        pageSize,
      };
    },
  });
};
